import {
    ProgramTypeNameEnum,
    RatchetModels,
    RatchetModelsEnum,
    Territory
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { ColDef, EditableCallbackParams, ICellRendererParams } from 'ag-grid-community';
import { isEmpty, sortBy } from 'lodash';
import { wmgBlendedRateGridColIdSuffix, wmgBlendedRateGridColNameSuffix } from 'src/constants/rateCards';
import { IMetadata } from 'src/types/IMetadata';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import { getCustomerTypeCamelStr, getProgramTypeCamelStr, getTerritoryCodeById } from 'src/util/grid/cellGetter';
import { getTerritoryFlagRenderer } from 'src/util/grid/cellRenderer';
import { dropUndefined } from 'src/util/list-utils';
import { getThresholdDisplayName, getThresholdFieldName } from 'src/util/thresholdUtil';

import { conversionModelsThresholdsMap } from '../AddContractTerms/AddConversionModel/conversionModelThresholds';

const baseDef = {
    cellStyle: { borderRight: '1px solid #dde2eb' },
    wrapHeaderText: true,
    autoHeaderHeight: true,
    valueFormatter: emptyCellFormatter
};

const selectBaseDef = {
    ...baseDef,
    cellEditor: 'agRichSelectCellEditor',
    cellEditorPopup: true
};

export const getCppMauColDefs = (metadata: IMetadata, conversionModels: RatchetModels = []): ColDef[] => {
    const territories: Territory[] = Object.values(metadata.territoryMapping);
    const CAEOptionList: string[] = dropUndefined(
        Array.from(new Set(Object.values(metadata.CaeMapping!).map((t) => t.contentAcquisitionEntityCode)))
    );
    const currencyOptionList: string[] = dropUndefined(
        Array.from(new Set(territories.flatMap((t) => [t.accrualCurrency, t.settlementCurrency, t.currency])))
    );

    const colDefs: ColDef[] = [
        {
            ...baseDef,
            field: 'territoryId',
            headerName: 'Territory',
            filter: 'agSetColumnFilter',
            valueGetter: getTerritoryCodeById(metadata.territoryMapping),
            cellRenderer: (params: ICellRendererParams) => getTerritoryFlagRenderer(params, true)
        },
        {
            ...selectBaseDef,
            field: 'rateCardCurrency',
            headerName: 'Rate card currency',
            filter: 'agSetColumnFilter',
            cellEditorParams: { values: currencyOptionList },
            editable: true
        },
        {
            ...selectBaseDef,
            field: 'settlementCurrency',
            headerName: 'Settlement currency',
            filter: 'agSetColumnFilter',
            cellEditorParams: { values: currencyOptionList },
            editable: true
        },
        {
            ...selectBaseDef,
            field: 'cae',
            headerName: 'CAE',
            filter: 'agSetColumnFilter',
            cellEditorParams: { values: CAEOptionList },
            editable: true
        },
        {
            ...baseDef,
            field: 'playType',
            headerName: 'Play type',
            filter: 'agSetColumnFilter',
            hide: true
        },
        {
            field: 'paperPlanName',
            headerName: 'Customer type',
            filter: 'agSetColumnFilter',
            valueGetter: getCustomerTypeCamelStr,
            hide: true
        },
        {
            ...baseDef,
            field: 'programType',
            headerName: 'Program type',
            filter: 'agSetColumnFilter',
            valueGetter: getProgramTypeCamelStr,
            hide: true
        },
        {
            ...baseDef,
            field: 'rate',
            headerName: 'Per play rate',
            editable: (params: EditableCallbackParams) =>
                params.data.programType !== ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY
        }
    ];

    if (!isEmpty(conversionModels)) {
        const conversion = sortBy(conversionModels, ['orderId'])[0];

        // Dynamically add ratchet cols
        if (
            conversion?.ratchetModel === RatchetModelsEnum.PRIME_CONVERSION_UMG ||
            conversion?.ratchetModel === RatchetModelsEnum.PRIME_CONVERSION_FLOOR_UMG
        ) {
            // remove default per play rate column def
            colDefs.pop();
            const umgRatchets = conversionModelsThresholdsMap[conversion?.ratchetModel];
            umgRatchets.forEach((ratchet, index) => {
                colDefs.push({
                    ...baseDef,
                    field: getThresholdFieldName(index + 1),
                    headerName: getThresholdDisplayName(umgRatchets[index]),
                    editable: (params: EditableCallbackParams) =>
                        params.data.programType !== ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY
                });
            });
        } else {
            conversion?.ratchets?.forEach((ratchet) => {
                colDefs.push({
                    ...baseDef,
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    field: getThresholdFieldName(ratchet.threshold!),
                    headerName: getThresholdDisplayName(ratchet),
                    editable: (params: EditableCallbackParams) =>
                        params.data.programType !== ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY
                });
            });
        }

        if (conversion?.ratchetModel === RatchetModelsEnum.PRIME_PRIMARY_CONVERSION_WMG) {
            const wmgTertiaryRatchets = conversionModelsThresholdsMap[RatchetModelsEnum.PRIME_TERTIARY_CONVERSION_WMG];
            wmgTertiaryRatchets.forEach((ratchet) => {
                colDefs.push({
                    ...baseDef,
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    field: getThresholdFieldName(ratchet.threshold!, wmgBlendedRateGridColIdSuffix),
                    headerName: getThresholdDisplayName(ratchet, wmgBlendedRateGridColNameSuffix),
                    editable: (params: EditableCallbackParams) =>
                        params.data.programType !== ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY
                });
            });
        }
    }

    return colDefs;
};
